import { config } from '../../../../config';
import DigiobcanApi, { type NewsCategory } from '../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import OverflowChips from '../../../components/OverflowChips';
import FormTextField from '../../../components/form/FormTextField';
import { getNewsCategories } from '../../../features/queries/getNewsCategories';
import { getNewsFeeds } from '../../../features/queries/getNewsFeeds';
import { shortId } from '../../../lib/ids';
import { useM, useQ } from '../../../lib/query';
import { useLayout } from '../../../providers/LayoutProvider';
import { useLogger } from '../../../providers/LoggerProvider';
import { useResponsive } from '../../../providers/ResponsiveProvider';
import {
  __,
  declension,
  required,
  toastError,
  toastSuccess,
} from '../../../utils';
import NewsFeedDialog from './NewsFeedDialog';
import NewsFeedsPanel from './NewsFeedsPanel';
import { isBusy, POLL_MS } from './newsFeeds';
import { encodePath, formatSourcePath } from './newsSources';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import { useAuthRoles } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChip,
  FilterPanel,
  Stack,
  Typography,
  useUrlFilters,
  type Column,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import { matchSorter, rankings } from 'match-sorter';
import React from 'react';
import { Form } from 'react-final-form';
import { useNavigate, useSearchParams } from 'react-router';

/** Stabilná referencia - `react-final-form` pri novej reinicializuje formulár. */
const CREATE_INITIAL_VALUES = { name: '', description: '' };

export const newsCategoryPath = (categoryId: string) =>
  `/administracia/kategorie-novinky/${shortId(categoryId)}`;

/**
 * Zoznam kategórií noviniek. Kategórie sú od migrácie 000025 ploché (sedem
 * koreňov), takže tu už nie je strom ani nadradená kategória. Riadok vedie
 * na detail - tam sa upravujú údaje aj zdroje; tlačidlá v riadku tu boli
 * a zbytočne sa bili o miesto so zdrojmi.
 */
const NewsCategoriesAdminPage: React.FC = () => {
  const { logger } = useLogger();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setActions, setFooter, setPageMeta, setAside } = useLayout();
  const { isMd } = useResponsive();

  // Hľadanie patrí k zoznamu a jeho hodnota do URL. Celý zoznam príde jedným
  // dotazom, takže sa filtruje na klientovi.
  const [searchParams, setSearchParams] = useSearchParams();
  const { query, setQuery, search } = useUrlFilters({
    searchParams,
    setSearchParams,
    debounce: 200,
  });
  const searchValue = search ?? '';

  const {
    data: newsCategories = [],
    isLoading: isLoadingNewsCategories,
    queryKey: newsQueryKey,
  } = useQ(getNewsCategories, { view: 'flat' });

  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const { hasPermission } = useAuthRoles();

  const canManage = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:news-categories:manage',
      }),
    [hasPermission],
  );

  // Zdroje noviniek sú v pravom stĺpci (na mobile v sheete). Kým niektorý
  // čaká alebo beží, stav sa obnovuje - aj v otvorenom detaile, ktorý si
  // záznam berie odtiaľto.
  const { data: newsFeeds = [], isLoading: isLoadingFeeds } = useQ(
    getNewsFeeds,
    undefined,
    {
      enabled: canManage,
      refetchInterval: (query) =>
        query.state.data?.some(isBusy) ? POLL_MS : false,
    },
  );
  const [openFeedId, setOpenFeedId] = React.useState<number | null>(null);
  const [feedsSheetOpen, setFeedsSheetOpen] = React.useState(false);
  const openFeed = React.useMemo(
    () => newsFeeds.find((feed) => feed.feed_id === openFeedId) ?? null,
    [newsFeeds, openFeedId],
  );
  const closeFeed = React.useCallback(() => setOpenFeedId(null), []);

  const createNewsCategoryMutation = useM(
    (values: { name: string; description?: string }) =>
      DigiobcanApi.createNewsCategory(undefined, values),
    {
      onSuccess: (newCategory) => {
        toastSuccess(__('Kategória bola vytvorená'));
        queryClient.invalidateQueries({ queryKey: newsQueryKey });
        // Nová kategória je bez zdrojov, a tie sa pridávajú na detaile.
        navigate(newsCategoryPath(newCategory.category_id));
      },
      onError: (e, variables) => {
        logger.error('Error creating news category', {
          action: 'createNewsCategory',
          error: e,
          data: { ...variables },
        });
        toastError(__('Niečo sa pokazilo'));
      },
    },
  );

  const deleteNewsCategoriesMutation = useM(
    (ids: string[]) =>
      Promise.all(ids.map((id) => DigiobcanApi.deleteNewsCategory(id))),
    {
      onSuccess: (_, ids) => {
        toastSuccess(
          ids.length === 1
            ? __('Kategória bola odstránená.')
            : __('Kategórie boli odstránené.'),
        );
        queryClient.setQueryData(newsQueryKey, (old: NewsCategory[] = []) =>
          old.filter((item) => !ids.includes(item.category_id)),
        );
      },
      onError: (e, ids) => {
        logger.error('Error deleting news categories', {
          action: 'deleteNewsCategories',
          error: e,
          data: { ids },
        });
        toastError(__('Kategórie sa nepodarilo odstrániť.'));
      },
    },
  );

  const handleCreate = async (values: {
    name: string;
    description?: string;
  }) => {
    if (!navigator.onLine) {
      toastError(__('Internetové pripojenie zlyhalo.'));
      return;
    }
    try {
      await createNewsCategoryMutation.mutateAsync({
        name: values.name,
        description: values.description || undefined,
      });
    } catch {
      // Chybu hlási mutácia.
    }
    setShowAddDialog(false);
  };

  const filteredResults = React.useMemo(() => {
    if (!searchValue) return newsCategories;
    return matchSorter(newsCategories, searchValue, {
      keys: ['category_id', 'name', 'description'],
      threshold: rankings.CONTAINS,
    });
  }, [newsCategories, searchValue]);

  const columns: Column<NewsCategory>[] = React.useMemo(
    () => [
      {
        key: 'category_id',
        label: __('ID'),
        width: '92px',
        minWidth: '92px',
        mono: true,
        render: (category) => shortId(category.category_id),
      },
      {
        key: 'name',
        label: __('Názov'),
        width: '1.3fr',
        minWidth: '220px',
        strong: true,
        // Popis pod názvom, nie za ikonou `i` - rovnako ako v kategóriách
        // podnetov: je to to, podľa čoho úradník pozná, čo do kategórie patrí.
        render: (category) => (
          <Stack gap={0.15} sx={{ minWidth: 0 }}>
            <Typography variant='body2'>{category.name}</Typography>
            {category.description ? (
              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {category.description}
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      {
        key: 'sources',
        label: __('Zdroje'),
        width: '2.7fr',
        minWidth: '240px',
        render: (category) => (
          <OverflowChips
            items={(category.sources ?? []).map((path) => ({
              key: encodePath(path),
              label: formatSourcePath(path),
            }))}
            empty={
              <Typography variant='body2' color='text.secondary'>
                {__('Žiadne')}
              </Typography>
            }
          />
        ),
      },
    ],
    [],
  );

  React.useEffect(() => {
    if (!canManage) {
      setActions([]);
      return;
    }

    // Vytvorenie je akcia stránky. Mazanie vybraných patrí k výberu, takže
    // je v pätičke pri jeho počte. Zdroje sú na desktope trvalý stĺpec, na
    // mobile ich otvára akcia.
    setActions([
      ...(isMd
        ? []
        : [
            {
              label: __('Zdroje'),
              onClick: () => setFeedsSheetOpen(true),
              variant: 'outline' as const,
              icon: <SyncIcon />,
            },
          ]),
      {
        label: __('Pridať kategóriu'),
        onClick: () => setShowAddDialog(true),
        variant: 'dark',
        icon: <AddIcon />,
      },
    ]);
  }, [canManage, isMd, setActions]);

  const feedsPanel = React.useMemo(
    () => (
      <NewsFeedsPanel
        feeds={newsFeeds}
        isLoading={isLoadingFeeds}
        onOpenFeed={(feed) => setOpenFeedId(feed.feed_id)}
      />
    ),
    [newsFeeds, isLoadingFeeds],
  );

  // Rovnako ako filter v správe podnetov: trvalý stĺpec napravo, ktorý
  // začína na úrovni drobčekov. Na mobile sa nezmestí, tam je sheet.
  React.useEffect(() => {
    if (!canManage || !isMd) {
      setAside(null);
      return;
    }
    setAside(
      <FilterPanel
        variant='inline'
        fields={[]}
        value={{}}
        title={__('Zdroje noviniek')}
      >
        {feedsPanel}
      </FilterPanel>,
    );
    return () => setAside(null);
  }, [canManage, isMd, setAside, feedsPanel]);

  React.useEffect(() => {
    if (!selectedRows.length) {
      setFooter(null);
      return;
    }

    setFooter(
      <Stack direction='row' alignItems='center' gap={1}>
        <Typography
          variant='body2'
          sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
        >
          {__('Vybrané:')} {selectedRows.length}
        </Typography>
        <Button
          size='small'
          variant='danger'
          onClick={() => setShowDeleteDialog(true)}
        >
          {__('Zmazať')}
        </Button>
        <Button size='small' variant='text' onClick={() => setSelectedRows([])}>
          {__('Zrušiť výber')}
        </Button>
      </Stack>,
    );

    return () => setFooter(null);
  }, [selectedRows, setFooter]);

  // Počet vedľa titulu je poctivý: zoznam sa načítava celý.
  React.useEffect(() => {
    setPageMeta(
      `${filteredResults.length} ${declension(filteredResults.length, [
        __('kategória'),
        __('kategórie'),
        __('kategórií'),
      ])}`,
    );
    return () => setPageMeta(null);
  }, [setPageMeta, filteredResults.length]);

  return (
    <>
      <FilterBar
        fields={[]}
        value={{}}
        query={query}
        onQueryChange={setQuery}
        searchPlaceholder={__('Hľadať podľa ID, názvu alebo popisu kategórie')}
      >
        {searchValue ? (
          <FilterChip
            label={__('Hľadaný výraz')}
            value={searchValue}
            onRemove={() => setQuery('')}
          />
        ) : null}
      </FilterBar>

      {/* Plátno kreslí shell, tu ostáva len odsadenie tabuľky od okrajov. */}
      <Box sx={{ padding: 1.5 }}>
        <DataTable
          variant={isMd ? 'table' : 'cards'}
          columns={columns}
          rows={filteredResults}
          rowKey='category_id'
          // Riadok vedie na detail kategórie. `rowHref`, nie `onRowClick`,
          // aby sa dal otvoriť aj cmd-klikom do novej karty.
          rowHref={(category) => newsCategoryPath(category.category_id)}
          selected={canManage ? selectedRows : undefined}
          onSelectionChange={
            canManage ? (next) => setSelectedRows(next.map(String)) : undefined
          }
          selectAllLabel={__('Označiť všetky kategórie')}
          selectRowLabel={__('Označiť kategóriu')}
          loading={isLoadingNewsCategories}
          empty={
            <EmptyState
              compact
              icon={searchValue ? 'search' : 'list'}
              title={
                searchValue
                  ? __('Žiadna kategória nesedí na hľadanie')
                  : __('Žiadne kategórie')
              }
              description={
                searchValue
                  ? __('Skúste iný výraz.')
                  : __('Kategóriu pridáš tlačidlom v hlavičke.')
              }
              actions={
                searchValue ? (
                  <Button
                    variant='outline'
                    size='small'
                    onClick={() => setQuery('')}
                  >
                    {__('Zrušiť hľadanie')}
                  </Button>
                ) : undefined
              }
            />
          }
        />
      </Box>

      <NewsFeedDialog feed={openFeed} onClose={closeFeed} />

      {feedsSheetOpen && !isMd ? (
        <FilterPanel
          variant='sheet'
          fields={[]}
          value={{}}
          title={__('Zdroje noviniek')}
          closeLabel={__('Zavrieť')}
          applyLabel={__('Zavrieť')}
          onClose={() => setFeedsSheetOpen(false)}
        >
          {feedsPanel}
        </FilterPanel>
      ) : null}

      <ConfirmationDialog
        open={showDeleteDialog}
        title={__('Zmazať vybrané kategórie?')}
        description={`${__('Ste si istý, že chcete zmazať')} ${selectedRows.length} ${__('vybraných kategórií? Odberatelia o ne prídu a novinky z ich zdrojov sa v nich prestanú zobrazovať.')}`}
        confirmLabel={__('Zmazať')}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={async () => {
          if (!navigator.onLine) {
            toastError(__('Internetové pripojenie zlyhalo.'));
            return;
          }
          try {
            await deleteNewsCategoriesMutation.mutateAsync(selectedRows);
            setSelectedRows([]);
          } catch {
            // Chybu hlási mutácia.
          }
          setShowDeleteDialog(false);
        }}
        isLoading={deleteNewsCategoriesMutation.isPending}
        fullWidth
        maxWidth='xs'
      />

      <ConfirmationDialog
        open={showAddDialog}
        confirmLabel={__('Vytvoriť')}
        title={__('Vytvoriť novú kategóriu')}
        formId='createForm'
        onClose={() => setShowAddDialog(false)}
        isLoading={createNewsCategoryMutation.isPending}
        fullWidth
        maxWidth='xs'
      >
        <Form onSubmit={handleCreate} initialValues={CREATE_INITIAL_VALUES}>
          {({ handleSubmit }) => (
            <form
              id='createForm'
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <FormTextField
                required
                label={__('Názov kategórie')}
                name='name'
                validate={required}
                maxLength={120}
              />
              <FormTextField
                label={__('Popis kategórie')}
                name='description'
                multiline
                rows={2}
                maxLength={500}
              />
            </form>
          )}
        </Form>
      </ConfirmationDialog>
    </>
  );
};

export default NewsCategoriesAdminPage;
